import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, font, sizing, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingSmall,
    HeadingXSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    EmailTextField,
    getSignupErrorMessageByCode,
    isEmailValid,
    PASSWORD_MIN_LENGTH,
    PasswordTextField,
    signupWithPassword,
} from "@khan/auth-password";
import {getContinueUrl} from "@khan/auth-shared";
import {standardTransition} from "@khan/shared-styles";
import {parseUrl, redirect, useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {BackLink} from "./back-link.tsx";
import {SignUpLegalCheckbox} from "./signup-legal-checkbox.tsx";
import {SignupOauthButtons} from "./signup-oauth-buttons.tsx";

// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export enum Roles {
    PARENT = "parent",
    TEACHER = "teacher",
    LEARNER = "learner",
}

interface Props {
    // The top level heading text to display
    heading: string;
    // Text which displays below the heading. Provides extra info to the user
    subHeading?: string;
    // The path to navigate to after a user signs up. Should be preceded by "/"
    nextPath: string;
    // The role the user is signing up as
    role: Roles;
    // The legal blurb that displays beneath the signup button
    legalBlurb: React.ReactNode;
    // The text for the back link
    backlinkText?: string;
    // The path for the back link. Note this is also used to redirect a user if
    // they do not have a birthday or the proper role.
    backlinkPath: string;
    // Hide the back link or not
    hideBackLink?: boolean;
    // Whether the current page is the homepage
    isHomepage?: boolean;
    maxWidth?: string;
    // Callback triggered after a user has signed up, but before they are
    // redirected to the next page.
    onSubmitSuccess?: () => Promise<void>;
    // If there isn't a continue= query param, use this path as the default
    defaultContinuePath?: string;
    // The class code to use for sign up.
    classCode?: string;
}

/**
 * A form for the user to enter their username and password during sign up.
 *
 * Also includes the option to sign up with a 3rd parties such as Google.
 */
export const SignupForm = ({
    heading,
    subHeading,
    nextPath,
    role,
    legalBlurb,
    backlinkText,
    backlinkPath,
    hideBackLink = false,
    isHomepage = false,
    maxWidth = "100%",
    onSubmitSuccess,
    defaultContinuePath = "/",
    classCode,
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [oauthError, setOauthError] = useState("");
    const [checked, setChecked] = useState(false);
    const [signupValidationError, setSignupValidationError] = useState("");
    const [oauthValidationError, setOauthValidationError] = useState("");
    const [signupError, setSignupError] = useState<string | React.ReactElement>(
        "",
    );
    const navigate = useNavigate();
    const location = useLocation();
    const {gqlClient} = useApolloGqlClient();
    const tosCheckboxFeatureFlagOn = useFlagOrCookie(
        "signup-tos-checkbox-required",
    );
    const birthdate = useMemo(() => {
        return sessionStorage.getItem("new_signup_birthday") ?? "";
    }, []);

    if (!birthdate && role === Roles.LEARNER) {
        navigate(backlinkPath);
        return;
    }

    const onEmailChange = (value: string) => {
        setEmail(value);
    };

    const onPasswordChange = (value: string) => {
        setPassword(value);
    };

    // Callback for SignupOauthButtons when TOS is not accepted
    const handleOAuthValidationError = () => {
        // Clear non oAuth erorrs
        setSignupError("");
        setSignupValidationError("");

        // Set TOS error
        setOauthValidationError(t`You must agree to the terms to proceed.`);
    };

    // Validates whether the user agreed to the terms
    const validateSignUpTerms = (): boolean => {
        // Skip validation if the TOS checkbox feature is turned off
        if (!tosCheckboxFeatureFlagOn) {
            return true;
        }
        if (!checked) {
            // Clear oAuth errors
            setOauthError("");
            setOauthValidationError("");

            // Set TOS error
            setSignupValidationError(
                t`You must agree to the terms to proceed.`,
            );
            return false;
        }

        // If form is valid clear previous form error
        setOauthValidationError("");
        setSignupValidationError("");
        return true;
    };

    const Heading = () => {
        return isHomepage ? (
            <HeadingLarge>{heading}</HeadingLarge>
        ) : (
            <HeadingSmall>{heading}</HeadingSmall>
        );
    };

    const finalRedirectUrl = getContinueUrl(location) ?? defaultContinuePath;
    const continueUrl = parseUrl(nextPath);
    const backUrl = parseUrl(backlinkPath);
    if (finalRedirectUrl && finalRedirectUrl !== defaultContinuePath) {
        continueUrl.searchParams.set("continue", finalRedirectUrl);
        backUrl.searchParams.set("continue", finalRedirectUrl);
    }

    const onSignup = async () => {
        // Validate terms agreement
        if (!validateSignUpTerms()) {
            return;
        }

        // Check if valid
        if (!isEmailValid(email) || password.length < PASSWORD_MIN_LENGTH) {
            setSignupError(t`Please fill out all required fields.`);
            return;
        }

        try {
            setLoading(true);
            await signupWithPassword(gqlClient, {
                email,
                password,
                firstname: "",
                lastname: "",
                role,
                birthdate,
                signupCodes: classCode ? [classCode] : undefined,
            });

            sessionStorage.removeItem("new_signup_birthday");

            if (onSubmitSuccess) {
                await onSubmitSuccess();
            }

            redirect(continueUrl.pathname + continueUrl.search);
        } catch (error) {
            if (error instanceof Error) {
                const message = getSignupErrorMessageByCode(error.message);
                setSignupError(message);
            } else {
                setSignupError("An error occurred, please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    const passwordTooltip = t`Passwords should be at least ${PASSWORD_MIN_LENGTH} characters long and should contain a mixture of letters, numbers, and other characters.`;

    return (
        <form
            action=""
            onSubmit={async (evt: React.SyntheticEvent): Promise<void> => {
                evt.preventDefault();
                await onSignup();
            }}
        >
            <View style={{maxWidth}}>
                {!hideBackLink && (
                    <>
                        <BackLink
                            text={backlinkText}
                            backUrl={backUrl.pathname + backUrl.search}
                            style={styles.backlink}
                        />
                        <Strut size={spacing.xSmall_8} />
                    </>
                )}
                <Heading />
                {subHeading && (
                    <>
                        <Strut size={spacing.xSmall_8} />
                        <LabelMedium style={styles.subHeading}>
                            {subHeading}
                        </LabelMedium>
                    </>
                )}
                <Strut size={spacing.medium_16} />
                {tosCheckboxFeatureFlagOn && (
                    <SignUpLegalCheckbox
                        checked={checked}
                        setChecked={setChecked}
                        legalBlurb={legalBlurb}
                    />
                )}
                <Strut size={spacing.medium_16} />
                <ul className={css(styles.buttons, styles.buttonsIconOnly)}>
                    <SignupOauthButtons
                        under13={false}
                        role={role}
                        continueUrl={continueUrl.toString()}
                        iconOnly={true}
                        size="large"
                        setServerError={setOauthError}
                        showFacebookButton={true}
                        validationRequired={tosCheckboxFeatureFlagOn}
                        isFormValid={checked}
                        onValidationError={handleOAuthValidationError}
                    />
                </ul>
                <View role="alert" aria-live="polite">
                    {oauthValidationError && (
                        <Body tag="p" style={styles.fieldError}>
                            {oauthValidationError}
                        </Body>
                    )}
                    {oauthError && (
                        <Body tag="p" style={styles.fieldError}>
                            {oauthError}
                        </Body>
                    )}
                </View>
                <Strut size={spacing.xLarge_32} />
                <View style={styles.fieldsHeader}>
                    <HeadingXSmall>{t`OR SIGN UP WITH EMAIL:`}</HeadingXSmall>
                    <LabelMedium>
                        <span className={css(styles.requiredAsterisk)}>*</span>
                        <span className={css(styles.requiredText)}>
                            {" "}
                            {t`required field`}
                        </span>
                    </LabelMedium>
                </View>
                <Strut size={spacing.large_24} />
                <EmailTextField
                    required={true}
                    onChange={onEmailChange}
                    label={t`Email`}
                    initialValue={email}
                />
                <Strut size={spacing.large_24} />
                <PasswordTextField
                    required={true}
                    label={t`Password`}
                    onChange={onPasswordChange}
                    initialValue={password}
                    tooltip={passwordTooltip}
                />
                <Strut size={spacing.large_24} />
                <Button
                    type="submit"
                    style={styles.signupButton}
                    spinner={loading}
                >
                    {t`Sign up`}
                </Button>
                {!tosCheckboxFeatureFlagOn && (
                    <>
                        <Strut size={spacing.large_24} />
                        {legalBlurb}
                    </>
                )}
                <View role="alert" aria-live="polite">
                    {signupValidationError && (
                        <Body tag="p" style={styles.fieldError}>
                            {signupValidationError}
                        </Body>
                    )}
                    {signupError && (
                        <Body tag="p" style={styles.fieldError}>
                            {signupError}
                        </Body>
                    )}
                </View>
            </View>
        </form>
    );
};

const styles = StyleSheet.create({
    backlink: {
        alignSelf: "flex-start",
    },
    subHeading: {color: color.offBlack64},
    fieldsHeader: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
    },
    requiredAsterisk: {
        color: color.red,
    },
    requiredText: {
        color: color.offBlack64,
    },
    signupButton: {
        width: "100%",
    },
    buttons: {
        // @ts-expect-error - TS2322 - Type '{ from: { opacity: number; }; to: { opacity: number; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            from: {opacity: 0},
            to: {opacity: 1},
        },
        animationDuration: "500ms",
        width: "100%",
    },
    buttonsIconOnly: {
        display: "flex",
        flexWrap: "wrap",
        columnGap: sizing.size_160,
        rowGap: sizing.size_080,
    },
    fieldError: {
        color: color.red,
        lineHeight: font.lineHeight.small,
        transition: standardTransition,
        transitionProperty: `max-height, opacity`,
        marginBlockStart: sizing.size_200,
        marginBlockEnd: sizing.size_0,
    },
});
